// encounter.ts

import lodash from "lodash";
import { useEffect, useRef, useState } from "react";
import { UseQueryOptions } from "react-query";

import { ResponseList } from "@imminently/react/store/query";
import { usePatientList } from "@/pages/home/patient-list/patient-slice";
import { createResourceService } from "@/query/createResourceService";
import { PubSubMessage } from "@/query/types";
import { filterAction } from "@/query/PubSubSubject";
import { getLocationsByAreas } from "@/resources/locationHelpers";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { Analytics, Note, PatientEncounter } from "@/resources/types";
import global, { formatName, usePatientNotification } from "@/global";
import { WORK_OFFLINE } from "@/resources/constants";
import { mockEncounter01 } from "@/util/printable-obschart/mocks/mockEncounter01";

const { dataProvider, queryClient, client } = global;

// Create mock encounters by cloning and modifying the mock encounter
const createMockEncounters = (count: number): PatientEncounter[] => {
  return Array.from({ length: count }, (_, i) => {
    const encounter = lodash.cloneDeep(mockEncounter01) as any;
    // Modify some fields to make each encounter unique
    encounter.id = `mock-encounter-${i}`;
    encounter.arrived = Date.now() - (i * 1000 * 60 * 30); // 30 minutes apart
    encounter.patient.firstName = `Testy${i}`;
    encounter.patient.lastName = `McTestface${i}`;
    encounter.status = i % 3 === 0 ? "seen" : i % 3 === 1 ? "triaged" : "submitted";

    // Ensure required properties are present
    if (!encounter.delegates) {
      encounter.delegates = [];
    }
    if (!encounter.healthcareDirective) {
      encounter.healthcareDirective = "";
    }
    if (!encounter.regularMeds) {
      encounter.regularMeds = [];
    }

    return encounter;
  });
};

const mockEncounters = createMockEncounters(20); // Create 20 mock encounters

// Create the base service
const baseService = createResourceService<PatientEncounter>({
  resource: "encounters",
  client,
  dataProvider,
  queryClient,
});

// Mock implementation of useResources
const useMockResources = ({ filter, pagination }: { filter: any; pagination: any }) => {
  const filtered = mockEncounters.filter(encounter => {
    // Basic filtering based on status
    if (filter.status && filter.status !== encounter.status) {
      return false;
    }

    // Search functionality - search in patient names using firstName and lastName
    const patient = encounter.patient;
    if (patient && typeof patient === "object") {
      if (filter.firstName) {
        const firstNameTerm = filter.firstName.replace(/\*/g, "").toLowerCase();
        if (!patient.firstName?.toLowerCase().includes(firstNameTerm)) {
          return false;
        }
      }

      if (filter.lastName) {
        const lastNameTerm = filter.lastName.replace(/\*/g, "").toLowerCase();
        if (!patient.lastName?.toLowerCase().includes(lastNameTerm)) {
          return false;
        }
      }
    }

    return true;
  });

  const start = pagination.page * pagination.perPage;
  const end = start + pagination.perPage;

  return {
    data: filtered.slice(start, end),
    total: filtered.length,
    query: {
      isLoading: false,
      isFetching: false,
      refetch: () => Promise.resolve(), // Return empty promise for refetch
    },
    pagination: {
      pagination,
      setPagination: () => { /* Mock implementation */ },
      setPage: () => { /* Mock implementation */ },
    },
  };
};

// Export the service with mocked or real implementation
export const encounterService = WORK_OFFLINE ? {
  ...baseService,
  useResources: useMockResources,
  useGetList: (options: any = {}) => {
    let filtered = mockEncounters;

    // Apply firstName and lastName filters if provided
    if (options.meta?.firstName || options.meta?.lastName) {
      filtered = mockEncounters.filter(encounter => {
        const patient = encounter.patient;
        if (patient && typeof patient === "object") {
          if (options.meta.firstName) {
            const firstNameTerm = options.meta.firstName.replace(/\*/g, "").toLowerCase();
            if (!patient.firstName?.toLowerCase().includes(firstNameTerm)) {
              return false;
            }
          }

          if (options.meta.lastName) {
            const lastNameTerm = options.meta.lastName.replace(/\*/g, "").toLowerCase();
            if (!patient.lastName?.toLowerCase().includes(lastNameTerm)) {
              return false;
            }
          }

          return true;
        }
        return false;
      });
    }

    return {
      data: { data: filtered, total: filtered.length },
      isLoading: false,
      isFetching: false,
      isFetched: true,
      refetch: () => Promise.resolve(), // Return empty promise for refetch
    };
  },
  useGetOne: () => ({
    data: mockEncounters[0],
    isLoading: false,
    isFetching: false,
    refetch: () => Promise.resolve(), // Return empty promise for refetch
  }),
  useCreate: () => ({
    mutateAsync: async () => mockEncounters[0],
    isLoading: false,
    isFetching: false,
  }),
  useUpdate: () => ({
    mutateAsync: async () => mockEncounters[0],
    isLoading: false,
    isFetching: false,
  }),
  useDelete: () => ({
    mutateAsync: async () => mockEncounters[0],
    isLoading: false,
    isFetching: false,
  }),
  pubsub$: {
    subscribe: () => ({ unsubscribe: () => { /* Mock unsubscribe */ } }),
    pipe: () => ({
      subscribe: () => ({ unsubscribe: () => { /* Mock unsubscribe */ } }),
    }),
  },
} : baseService;

const { useSubresource, useGetCustom, pubsub$, handleResourceMessage, usePostCustom } = encounterService;

// export const generateNote = encounterService.endpoint

// TODO this needs to be moved into a context so we can pause/resume it
// ideally something generic
// should the context provider take the sub method?
// if its on the sub method, then it would imply multiple subs
// is that what we would want? thats what the pubsub$ is for
export const useLiveEncounters = () => {
  const notify = usePatientNotification();

  useEffect(() => {
    const sub = pubsub$.subscribe(handleResourceMessage);
    return () => {
      console.log("[useLiveEncounters] unsubscribing resource updater...");
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // the encounter service pubsub can also emit other data types
    const sub = encounterService.pubsub$
      // @ts-ignore
      .pipe<PubSubMessage<PatientEncounter>>(filterAction("update"))
      .subscribe((msg) => {
        const { data } = msg;
        // This is very hard to determine, but for some reason we dont get lastDistress
        // when they submit a distress. So abusing that for now.
        if (data.distressed && !data.lastDistress) {
          notify({
            message: "Distress",
            type: "error",
            when: data.lastModified as string,
            patientName: formatName(data.patient.firstName, data.patient.lastName),
            encounterId: data.id,
          });
        }
      });

    return () => {
      console.log("[useLiveEncounters] unsubscribing distress listener...");
      sub.unsubscribe();
    };
  }, [notify]);

  return null;
};

export const useNoteService = (encounterId: string) => useSubresource<Note>("notes", encounterId);

export type NextObservations = ResponseList<PatientEncounter>;

export const useNextObservations = (
  options?: UseQueryOptions<NextObservations>,
  filters?: any
) => {
  const { data, ...query } = useGetCustom<NextObservations>("nextobs", {
    ...options,
    meta: { params: filters },
  });

  return { data: data?.data, total: data?.total, ...query };
};

export const fetchPrintableObsChart = async (encounterId: string, observationsChartOnly: boolean) => {

  const printService = usePostCustom("printobs");

  const resp = await printService.mutateAsync({ encounterId, obsOnly: observationsChartOnly });

  console.log("printable obs chart response", resp);
};

export const useLiveNextObservations = () => {
  const { filters: localFilters } = usePatientList();
  const { settings } = useSettingsContext();
  const locations = ((settings as any)?.locations ?? []) as any[];
  const filters: any = {};

  if (localFilters.area.length > 0) {
    const locationShortNames = getLocationsByAreas(locations, localFilters.area);
    if (locationShortNames.length > 0) filters["location.shortName"] = locationShortNames.join(",");
  }

  const oldFilters = useRef(filters);
  const queryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: 5000,
  };

  const { data, ...query } = useNextObservations(queryOptions, filters);

  // Need to refetch when the filters change
  useEffect(() => {
    if (lodash.isEqual(filters, oldFilters.current)) return;

    oldFilters.current = filters;
    query.refetch();
  }, [filters]);

  return { data: data || [], ...query };

  // NOTE Using pubsub for next observations has been disabled as it does not
  // support location filtering
};

type AnalyticsMsg = PubSubMessage<Analytics>;

export const useAnalytics = (options?: UseQueryOptions<Analytics>) => {
  const [state, setState] = useState<Analytics>(null as unknown as Analytics);
  const { data, ...query } = useGetCustom<Analytics>("analytics", options);

  useEffect(() => {
    // the encounter service pubsub can also emit other data types
    const sub = encounterService.pubsub$
      // @ts-ignore
      .pipe<AnalyticsMsg>(filterAction("analytics"))
      .subscribe((msg) => {
        setState(msg.data);
      });
    return () => {
      console.log("[useAnalytics] unsubscribing...");
      sub.unsubscribe();
    };
  }, [setState]);

  useEffect(() => {
    if (data) {
      setState(data);
    }
  }, [data]);

  return { data: state, ...query };
};
// WIP an attempt to use a context

// type SubscribeFn<T> = (fn: PartialObserver<T> | ((value: T) => void)) => void;

// type SubscriptionContext<T> = {
//   subscribe: SubscribeFn<T>;
//   unsubscribe: () => void;
// }

// type EncounterMessage = PubSubMessage<PatientEncounter>;

// const EncounterSubscriptionContext = createContext<SubscriptionContext<EncounterMessage>>(null as unknown as SubscriptionContext<EncounterMessage>);

// a react context provider that includes a subscribe and unsubscribe function to pubsub$
// the subscribe function uses handeResourceMessage
// the unsubscribe function uses the unsubscribe function from the pubsub$ subscription
// const EncounterSubscriptionProvider = ({ children }: PropsWithChildren) => {
//   const [sub, setSub] = useState<Subscription | null>(null);

//   const subscribe: SubscribeFn = (fn) => {
//     const sub = pubsub$.subscribe(fn);
//     setSub(sub);
//   };

//   const unsubscribe = () => {
//     if (sub) {
//       sub.unsubscribe();
//       setSub(null);
//     }
//   };

//   const context = { subscribe, unsubscribe };

//   return (
//     <EncounterSubscriptionContext.Provider value={context}>
//       {children}
//     </EncounterSubscriptionContext.Provider>
//   );
// };


